import React from 'react';
import { ShieldCheck } from 'lucide-react';
import botspotLogo from '../../../assets/botspot_logo_horizontal.png';
import heroBacktestChart from '../../../assets/case-studies/home-hero-backtest-chart.jpg';
import type { AuthFirstDesignProps } from './types';
import './TacticalLedger.css';

const defaultContent = {
  evidenceDescription: 'Historical results stay with each bot revision.',
  evidenceLabel: 'Backtest evidence',
  evidenceMeta: 'Example run',
  imageAlt: 'Example historical backtest equity curve with trade markers',
  imageSrc: heroBacktestChart,
  mobileContext: 'Build custom bots, test them on historical data, and run them with your broker.',
  note: 'You approve every paper or live run.',
  steps: ['Build with AI', 'Test on history', 'Run with your broker'] as [string, string, string],
  title: 'Turn your trading rules into a bot.',
};

export default function TacticalLedger({
  authContent,
  content = defaultContent,
  subheading,
  titleId,
}: AuthFirstDesignProps): React.ReactElement {
  return (
    <div className="auth-first-design--tactical-ledger">
      <div className="tactical-ledger__frame">
        <header className="tactical-ledger__masthead">
          <a aria-label="BotSpot home" className="tactical-ledger__brand" href="/">
            <img
              alt="BotSpot by LumiWealth"
              className="tactical-ledger__logo"
              height={429}
              src={botspotLogo}
              width={1385}
            />
          </a>
          <span className="tactical-ledger__masthead-copy">
            botspot.trade
          </span>
        </header>

        <div className="tactical-ledger__grid">
          <div className="tactical-ledger__product-column">
            <div className="tactical-ledger__intro">
              <h1 className="tactical-ledger__title" id={titleId}>
                {content.title}
              </h1>
              <div className="tactical-ledger__subheading-container">{subheading}</div>
            </div>

            <figure className="tactical-ledger__evidence">
              <div className="tactical-ledger__evidence-header">
                <div>
                  <strong>{content.evidenceLabel}</strong>
                  <span>{content.evidenceDescription}</span>
                </div>
                <span>{content.evidenceMeta}</span>
              </div>
              <div
                aria-label={content.imageAlt}
                className="tactical-ledger__chart"
                role="img"
              >
                <img alt="" src={content.imageSrc} />
              </div>
              <figcaption>
                {content.steps.map((step) => <span key={step}>{step}</span>)}
              </figcaption>
            </figure>

            <div className="tactical-ledger__note-container">
              <ShieldCheck
                aria-hidden="true"
                className="tactical-ledger__note-icon"
                focusable="false"
                size={14}
                strokeWidth={1.8}
              />
              <span className="tactical-ledger__note-text">
                {content.note}
              </span>
            </div>
          </div>

          <div className="tactical-ledger__auth-column">
            <div className="tactical-ledger__mobile-context">
              <a aria-label="BotSpot home" className="tactical-ledger__mobile-brand" href="/">
                <img alt="BotSpot by LumiWealth" height={429} src={botspotLogo} width={1385} />
              </a>
              <p>{content.mobileContext}</p>
            </div>
            <div className="tactical-ledger__auth-body">{authContent}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
